import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Image,
  Trash2,
  Copy,
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  Loader2,
  MessageSquare,
  Save,
  Check,
  ExternalLink,
  FolderOpen,
  CheckSquare,
  Square,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ImageGalleryPopup({ orderId, orderInfo, isOpen, onClose, onImagesUpdated }) {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCaption, setEditingCaption] = useState(null);
  const [captionText, setCaptionText] = useState("");
  const [savingCaption, setSavingCaption] = useState(false);
  const [viewingImageIndex, setViewingImageIndex] = useState(null);
  const [folderPath, setFolderPath] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    // Reset ALL state when dialog opens or orderId changes
    if (isOpen) {
      setImages([]);
      setSelectedImages([]);
      setFolderPath(null);
      setViewingImageIndex(null);
      setEditingCaption(null);
      setCaptionText("");
      if (orderId) {
        fetchImages();
      }
    }
  }, [isOpen, orderId]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/orders/${orderId}/images`);
      setImages(response.data.images || []);
      
      // Extract folder path from first image if available
      if (response.data.images && response.data.images.length > 0) {
        const firstImagePath = response.data.images[0].path;
        const match = firstImagePath.match(/\/api\/images\/([^/]+\/[^/]+)\//);
        if (match) {
          setFolderPath(match[1]);
        }
      }
    } catch (error) {
      console.error("Error fetching images:", error);
      toast.error("Failed to load images");
    } finally {
      setLoading(false);
    }
  };

  const openInGallery = () => {
    // Use phone number from orderInfo prop passed from OrderSidebar
    const phone = orderInfo?.phone_number;
    if (phone) {
      navigate(`/images?search=${encodeURIComponent(phone)}`);
      onClose();
    } else if (folderPath) {
      navigate(`/images?folder=${encodeURIComponent(folderPath)}`);
      onClose();
    } else {
      toast.error("Phone number not available");
    }
  };

  // Check if we can navigate to gallery (have phone or folder path)
  const canOpenInGallery = orderInfo?.phone_number || folderPath;

  const handleDeleteImage = async (filename) => {
    try {
      await axios.delete(`${API}/orders/${orderId}/images/${filename}`);
      toast.success("Image moved to trash");
      fetchImages();
      onImagesUpdated?.();
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Failed to delete image");
    }
  };

  // Selection handlers
  const toggleImageSelection = (imagePath) => {
    setSelectedImages(prev => 
      prev.includes(imagePath)
        ? prev.filter(p => p !== imagePath)
        : [...prev, imagePath]
    );
  };

  const toggleSelectAll = () => {
    if (selectedImages.length === images.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages(images.map(img => img.filename));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedImages.length === 0) return;
    
    try {
      setBulkDeleting(true);
      let successCount = 0;
      let errorCount = 0;
      
      for (const filename of selectedImages) {
        try {
          await axios.delete(`${API}/orders/${orderId}/images/${filename}`);
          successCount++;
        } catch (error) {
          console.error(`Error deleting ${filename}:`, error);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} image(s) moved to trash`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to delete ${errorCount} image(s)`);
      }
      
      setSelectedImages([]);
      fetchImages();
      onImagesUpdated?.();
    } catch (error) {
      console.error("Error in bulk delete:", error);
      toast.error("Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const [duplicating, setDuplicating] = useState(false);

  const handleDuplicate = async () => {
    if (selectedImages.length === 0) return;
    setDuplicating(true);
    try {
      const imagePaths = selectedImages.map(filename =>
        `/var/www/thunderbuddy/backend/storage/images/${folderPath}/${filename}`
      );
      const response = await axios.post(`${API}/orders/${orderId}/duplicate-images`, {
        image_paths: imagePaths
      });
      toast.success(`Duplicated ${response.data.duplicated} image(s)`);
      setSelectedImages([]);
      fetchImages();
      onImagesUpdated?.();
    } catch (error) {
      toast.error("Failed to duplicate images");
    } finally {
      setDuplicating(false);
    }
  };

  const isAllSelected = images.length > 0 && selectedImages.length === images.length;

  const handleStartEditCaption = (image) => {
    setEditingCaption(image.path);
    setCaptionText(image.caption || "");
  };

  const handleSaveCaption = async (imagePath) => {
    if (!orderId) {
      toast.error("Order ID not found");
      return;
    }
    try {
      setSavingCaption(true);
      const response = await axios.put(
        `${API}/orders/${orderId}/images/caption`,
        { caption: captionText },
        { params: { image_path: imagePath } }
      );
      if (response.data.success) {
        toast.success("Caption saved!");
        setEditingCaption(null);
        setCaptionText("");
        fetchImages();
      }
    } catch (error) {
      console.error("Error saving caption:", error);
      toast.error(error.response?.data?.detail || "Failed to save caption");
    } finally {
      setSavingCaption(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingCaption(null);
    setCaptionText("");
  };

  // Count images with captions
  const captionedCount = images.filter(img => img.caption && img.caption.trim()).length;

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh]" data-testid="image-gallery-popup">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Image className="h-5 w-5 text-primary" />
              Order Images
              <Badge variant="outline" className="ml-2">{images.length} images</Badge>
              {captionedCount > 0 && (
                <Badge variant="secondary" className="bg-green-100 text-green-700">
                  {captionedCount} captioned
                </Badge>
              )}
            </DialogTitle>
            <div className="flex items-center gap-2">
              {/* Bulk Duplicate Button */}
              {selectedImages.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1.5 text-green-600 border-green-300 bg-green-50 hover:bg-green-100"
                  onClick={handleDuplicate}
                  disabled={duplicating}
                >
                  {duplicating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  Duplicate ({selectedImages.length})
                </Button>
              )}
              {/* Bulk Delete Button */}
              {selectedImages.length > 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      size="sm"
                      className="flex items-center gap-1.5"
                      disabled={bulkDeleting}
                      data-testid="bulk-delete-btn"
                    >
                      {bulkDeleting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                      Delete ({selectedImages.length})
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {selectedImages.length} Images</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will move {selectedImages.length} selected image(s) to trash. You can restore them later if needed.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleBulkDelete}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Delete All Selected
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              {canOpenInGallery && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1.5 text-blue-600 border-blue-300 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 transition-colors"
                  onClick={openInGallery}
                  data-testid="open-in-gallery-btn"
                >
                  <FolderOpen className="h-4 w-4" />
                  Open in Gallery
                  <ExternalLink className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
          {/* Select All / Deselect All */}
          {images.length > 0 && (
            <div className="flex items-center gap-2 pt-2 border-t mt-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs flex items-center gap-1.5 hover:bg-slate-100"
                onClick={toggleSelectAll}
                data-testid="select-all-btn"
              >
                {isAllSelected ? (
                  <>
                    <CheckSquare className="h-4 w-4 text-blue-600" />
                    Deselect All
                  </>
                ) : (
                  <>
                    <Square className="h-4 w-4 text-slate-400" />
                    Select All
                  </>
                )}
              </Button>
              {selectedImages.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {selectedImages.length} selected
                </Badge>
              )}
            </div>
          )}
        </DialogHeader>

        <ScrollArea className="h-[70vh] pr-4">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : images.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
              <Image className="h-16 w-16 mb-4 opacity-30" />
              <p>No images found for this order</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
              {images.map((image, index) => {
                const isSelected = selectedImages.includes(image.filename);
                return (
                <div
                  key={image.path}
                  className={`group relative bg-muted rounded-lg overflow-hidden border-2 transition-all ${
                    isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-transparent hover:border-slate-300'
                  }`}
                  data-testid={`image-item-${index}`}
                >
                  {/* Image - smaller thumbnail */}
                  <div className="aspect-square relative">
                    <img
                      src={`${BACKEND_URL}${image.path}`}
                      alt={image.filename}
                      loading="lazy"
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => setViewingImageIndex(index)}
                    />
                    
                    {/* Selection checkbox */}
                    <div 
                      className={`absolute top-1 left-1 z-10 ${isSelected || 'opacity-0 group-hover:opacity-100'} transition-opacity`}
                    >
                      <div
                        className={`h-7 w-7 rounded flex items-center justify-center cursor-pointer ${
                          isSelected 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-black/50 text-white hover:bg-black/70'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleImageSelection(image.filename);
                        }}
                        data-testid={`select-image-${index}`}
                      >
                        {isSelected && <Check className="h-5 w-5" />}
                      </div>
                    </div>
                    
                    {/* Caption indicator badge */}
                    {image.caption && image.caption.trim() && (
                      <div className="absolute top-1 right-1">
                        <Badge className="h-5 px-1 bg-green-500 text-white text-[10px]">
                          <MessageSquare className="h-3 w-3" />
                        </Badge>
                      </div>
                    )}

                    {/* Overlay with delete action */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            size="sm" 
                            variant="destructive" 
                            className="h-7 w-7 p-0 pointer-events-auto"
                            data-testid={`delete-image-${index}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Image</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will move the image to trash. You can restore it later if needed.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteImage(image.filename)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>

                  {/* Image number badge - bottom right */}
                  <div className="absolute bottom-12 right-1">
                    <Badge variant="secondary" className="text-[9px] h-4 px-1 bg-black/70 text-white">
                      #{index + 1}
                    </Badge>
                  </div>

                  {/* Caption Section - Compact */}
                  <div className="p-1.5 bg-background border-t">
                    {editingCaption === image.path ? (
                      <div className="space-y-1">
                        <Input
                          value={captionText}
                          onChange={(e) => setCaptionText(e.target.value.slice(0, 100))}
                          placeholder="Caption (max 100)"
                          className="h-6 text-[10px] px-1.5"
                          maxLength={100}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSaveCaption(image.path);
                            }
                            if (e.key === 'Escape') {
                              handleCancelEdit();
                            }
                          }}
                        />
                        <div className="flex items-center justify-between">
                          <span className="text-[8px] text-muted-foreground">
                            {captionText.length}/100
                          </span>
                          <div className="flex gap-0.5">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-5 w-5 p-0"
                              onClick={handleCancelEdit}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              className="h-5 w-5 p-0 bg-green-600 hover:bg-green-700"
                              onClick={() => handleSaveCaption(image.path)}
                              disabled={savingCaption}
                            >
                              {savingCaption ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Check className="h-3 w-3" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5 min-h-[20px] flex items-center"
                        onClick={() => handleStartEditCaption(image)}
                      >
                        {image.caption && image.caption.trim() ? (
                          <p className="text-[10px] line-clamp-2 text-foreground font-medium">{image.caption}</p>
                        ) : (
                          <p className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                            <MessageSquare className="h-2.5 w-2.5" />
                            Add caption
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Footer with image count */}
        {images.length > 0 && (
          <div className="flex items-center justify-between pt-2 border-t text-xs text-muted-foreground">
            <span>Total: {images.length} images</span>
            <span>{captionedCount} with captions</span>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Image Lightbox with Slider */}
    {viewingImageIndex !== null && images[viewingImageIndex] && (
      <div 
        className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center"
        onClick={() => setViewingImageIndex(null)}
      >
        {/* Close button */}
        <button
          className="absolute top-4 right-4 text-white text-3xl hover:text-gray-300 z-50"
          onClick={(e) => {
            e.stopPropagation();
            setViewingImageIndex(null);
          }}
        >
          ✕
        </button>
        
        {/* Image counter */}
        <div className="absolute top-4 left-4 text-white text-lg">
          {viewingImageIndex + 1} / {images.length}
        </div>

        {/* Previous button */}
        {viewingImageIndex > 0 && (
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full z-50"
            onClick={(e) => {
              e.stopPropagation();
              setViewingImageIndex(viewingImageIndex - 1);
            }}
          >
            <ChevronLeft className="h-8 w-8" />
          </button>
        )}

        {/* Next button */}
        {viewingImageIndex < images.length - 1 && (
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full z-50"
            onClick={(e) => {
              e.stopPropagation();
              setViewingImageIndex(viewingImageIndex + 1);
            }}
          >
            <ChevronRight className="h-8 w-8" />
          </button>
        )}

        {/* Main image */}
        <img
          src={`${BACKEND_URL}${images[viewingImageIndex].path}`}
          alt={images[viewingImageIndex].filename}
          className="max-w-[90vw] max-h-[85vh] object-contain"
          onClick={(e) => e.stopPropagation()}
        />
        
        {/* Caption if exists */}
        {images[viewingImageIndex].caption && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-lg max-w-[80vw]">
            {images[viewingImageIndex].caption}
          </div>
        )}
      </div>
    )}
    </>
  );
}
